/**
 * frontend/src/pages/valuation/_stepContext.ts
 *
 * VAL-PATH-ROUTES (P1) — the role + engagement ValuationStepGuard read, handed
 * down so the step bar marks the same steps (and locks) the left nav does.
 * Null outside a guard (screen tests, ProfileSelect): the bar then shows the
 * owner path with nothing locked.
 *
 * VAL-PATH-FOOTER (P2a) — the mover: the step's StepFooter registers its
 * save-first move here, and a step-bar click goes through it, so the bar
 * never leaves a step without saving it (U2). No footer, no mover: the bar
 * link navigates as a plain link.
 */
import { createContext, createElement, useContext, useRef, type MutableRefObject, type ReactNode } from 'react'
import type { ValuationEngagement } from '@/types/valuation'

export interface StepContext {
  role: string | undefined
  engagement: ValuationEngagement | null
}

export const StepCtx = createContext<StepContext | null>(null)

export function useStepContext(): StepContext | null {
  return useContext(StepCtx)
}

/** What a step's save reports: whether anything was sent, and the last save time. */
export interface StepSaveResult {
  changed: boolean
  savedAt: string | null
}

/**
 * Thrown by a step's save when the screen itself blocked it and already shows
 * why (Company's live "We couldn't save yet" line): the footer stays on the
 * step and adds no message of its own.
 */
export class StepBlocked extends Error {}

/** Save the open step, then go to `to`; stays put (and shows why) on failure. */
export type StepMover = (to: string) => Promise<void>

const StepMoveCtx = createContext<MutableRefObject<StepMover | null> | null>(null)

export function StepMoveProvider({ children }: { children: ReactNode }) {
  const mover = useRef<StepMover | null>(null)
  return createElement(StepMoveCtx.Provider, { value: mover }, children)
}

export function useStepMover(): MutableRefObject<StepMover | null> | null {
  return useContext(StepMoveCtx)
}
